import { AfterViewInit, Component, ElementRef, inject, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import KeenSlider, { KeenSliderInstance } from 'keen-slider';
import { HotToastService } from '@ngxpert/hot-toast';
import { AuthenticationService } from '@app/auth';
import { Logger } from '../../@core/services/misc/logger.service'; // this relative path is important for this page module to render correctly

const log = new Logger('LoginComponent');

@UntilDestroy()
@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
})
export class LoginComponent implements AfterViewInit, OnDestroy {
  showPassword = false;
  password = '';
  selectedLanguage = 'de';
  @ViewChild('sliderRef') sliderRef: ElementRef<HTMLElement>;
  @ViewChild('imageSliderRef') imageSliderRef: ElementRef<HTMLElement>;
  imageSources: string[] = ['assets/images/login/slide-image-1.jpg', 'assets/images/login/slide-image-2.jpg', 'assets/images/login/slide-image-3.jpg', 'assets/images/login/slide-image-4.jpg'];
  currentSlide = 1;
  slider: KeenSliderInstance = null;
  imageSlider: KeenSliderInstance = null;
  form: FormGroup;
  isLoading = false;
  protected readonly top = top;
  private readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _authenticationService = inject(AuthenticationService);
  private readonly _translateService = inject(TranslateService);
  private readonly _toast = inject(HotToastService);

  constructor() {
    this._createForm();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this._initImageSlider();
      this._initTextSlider();
    }, 500);
  }

  ngOnDestroy() {
    if (this.slider) this.slider.destroy();
    if (this.imageSlider) this.imageSlider.destroy();
  }

  login() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.isLoading = true;
    const login$ = this._authenticationService.login(this.form.value);
    this.form.disable();
    login$
      .pipe(
        finalize(() => {
          this.form.markAsPristine();
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: (credentials) => {
          log.info(`${credentials.username} successfully logged in`);
          this._toast.success(this._translateService.instant('Login Successful'));
          setTimeout(() => {
            this._router.navigate([this._route.snapshot.queryParams['redirect'] || '/dashboard'], { replaceUrl: true }).then(() => {
              this._toast.success(`${this._translateService.instant('Welcome')} ${credentials.firstName} ${credentials.lastName}`);
              this.isLoading = false;
            });
          }, 2000);
        },
        error: (err) => {
          const errorMessage = err?.error?.message || this._translateService.instant('Login failed. Please check your credentials and try again.');
          console.error(`Login error: ${errorMessage}`);
          this._toast.error(errorMessage || this._translateService.instant('Something went wrong'));
          this.isLoading = false;
          this.form.enable();
        },
      });
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  private _createForm() {
    this.form = this._formBuilder.group({
      username: ['', Validators.required],
      password: ['', Validators.required],
      remember: true,
    });
  }

  private _initImageSlider() {
    this.imageSlider = new KeenSlider(
      this.imageSliderRef.nativeElement,
      {
        loop: true,
      },
      [
        (slider) => {
          let timeout: any;
          let mouseOver = false;

          function clearNextTimeout() {
            clearTimeout(timeout);
          }

          function nextTimeout() {
            clearTimeout(timeout);
            if (mouseOver) return;
            timeout = setTimeout(() => {
              slider.next();
            }, 5000);
          }

          slider.on('created', () => {
            slider.container.addEventListener('mouseover', () => {
              mouseOver = true;
              clearNextTimeout();
            });
            slider.container.addEventListener('mouseout', () => {
              mouseOver = false;
              nextTimeout();
            });
            nextTimeout();
          });
          slider.on('dragStarted', clearNextTimeout);
          slider.on('animationEnded', nextTimeout);
          slider.on('updated', nextTimeout);
        },
      ],
    );
  }

  private _initTextSlider() {
    this.slider = new KeenSlider(this.sliderRef.nativeElement, {
      initial: this.currentSlide,
      slideChanged: (s) => {
        this.currentSlide = s.track.details.rel;
      },
    });
  }
}
